import { useState } from 'react';
import { motion } from 'framer-motion';
import { Facebook, Instagram, Mail, MessageCircle, Music2, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import SeoHead from '@/components/SeoHead';

const socials = [
  {
    label: 'Facebook',
    href: 'https://web.facebook.com/cvsix.collection',
    handle: 'cvsix.collection',
    Icon: Facebook,
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@trendyquesty',
    handle: '@trendyquesty',
    Icon: Music2,
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/trend_yquest/',
    handle: '@trend_yquest',
    Icon: Instagram,
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    message: '',
  });

  const handleSubmit = (event) => {
    event.preventDefault();

    const payload = `Hi Trendy Quest!%0A%0AName: ${encodeURIComponent(form.name)}%0APhone: ${encodeURIComponent(
      form.phone
    )}%0AMessage: ${encodeURIComponent(form.message)}`;

    window.open(`https://wa.me/254717002340?text=${payload}`, '_blank', 'noopener,noreferrer');
    toast.success('Opening WhatsApp so you can send your message.');
    setForm({ name: '', phone: '', message: '' });
  };

  return (
    <div className="bg-[#fffdf7]">
      <SeoHead
        title="Contact Trendy Quest Collection | WhatsApp, Socials and Store Location"
        description="Contact Trendy Quest Collection for orders, sizing help, delivery questions, and product enquiries. Reach us on WhatsApp, Instagram, TikTok, or visit our Nairobi store."
        path="/contact"
      />
      <section className="w-full px-4 py-12 md:px-6 md:py-16 lg:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid gap-10 lg:grid-cols-[1fr_1fr]"
        >
          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.22em] text-green">Contact Us</p>
            <h1 className="mb-5 text-3xl font-playfair font-bold text-gray-900 sm:text-4xl md:mb-6 md:text-5xl lg:text-6xl">
              Get in touch with Trendy Quest
            </h1>
            <p className="max-w-2xl text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
              We would love to hear from you. Reach out for product questions, orders, sizing help, or general enquiries.
            </p>

            <div className="mt-10 space-y-5">
              <div className="rounded-3xl border border-[#ebe3ce] bg-white p-6 shadow-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-green">About Us</p>
                <p className="mt-3 text-gray-700 leading-7">
                  At Trendy Quest Collection, we bring you quality footwear that blends style, comfort, and everyday practicality.
                  Our mission is simple—help you step out confidently, every single day.
                </p>
              </div>

              <div className="rounded-3xl border border-[#ebe3ce] bg-white p-6 shadow-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-green">Direct Contact</p>
                <div className="mt-3 space-y-2 text-gray-700">
                  <a href="tel:+254717002340" className="block text-gray-700 transition hover:text-green">Phone / WhatsApp: +254 717 002 340</a>
                  <a href="mailto:pattohkarimi@gmail.com" className="block text-gray-700 transition hover:text-green">Email: pattohkarimi@gmail.com</a>
                  <p>Address: Dubai Merchant Mall, along Ronald Ngala Street, First Floor, Shop G18</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-[1.35rem] border border-[#ebe3ce] bg-white p-4 shadow-[0_20px_50px_rgba(20,68,37,0.08)] sm:rounded-[2rem] sm:p-8">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-green">Send a Message</p>
              <h2 className="mt-3 text-3xl font-playfair font-bold text-gray-900">Contact form</h2>
              <p className="mt-2 text-gray-600">
                Fill this in and we will open WhatsApp with your message ready to send.
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-sm font-semibold text-gray-700">
                    Your Name
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    value={form.name}
                    onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                    className="w-full rounded-2xl border border-[#e6dcc8] bg-[#fffdf9] px-5 py-4 text-gray-900 outline-none transition focus:border-[#295c33] focus:ring-2 focus:ring-[#dfeedd]"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="contact-phone" className="mb-2 block text-sm font-semibold text-gray-700">
                    Phone Number
                  </label>
                  <input
                    id="contact-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
                    className="w-full rounded-2xl border border-[#e6dcc8] bg-[#fffdf9] px-5 py-4 text-gray-900 outline-none transition focus:border-[#295c33] focus:ring-2 focus:ring-[#dfeedd]"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="mb-2 block text-sm font-semibold text-gray-700">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    value={form.message}
                    onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))}
                    rows={5}
                    className="w-full rounded-2xl border border-[#e6dcc8] bg-[#fffdf9] px-5 py-4 text-gray-900 outline-none transition focus:border-[#295c33] focus:ring-2 focus:ring-[#dfeedd]"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-semibold text-white transition hover:bg-[#1ebe5d]"
                >
                  <MessageCircle size={18} />
                  Send via WhatsApp
                </button>
              </form>
            </div>

            <div className="rounded-[1.35rem] border border-[#ebe3ce] bg-white p-4 shadow-[0_20px_50px_rgba(20,68,37,0.08)] sm:rounded-[2rem] sm:p-8">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-green">Social Media</p>
              <div className="mt-6 space-y-4">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between rounded-2xl border border-[#ebe3ce] bg-[#f8faf6] px-5 py-4 transition hover:border-green hover:bg-white"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-green shadow-sm ring-1 ring-[#e7dcc0]">
                      <social.Icon size={20} strokeWidth={1.9} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{social.label}</p>
                      <p className="text-sm text-gray-600">{social.handle}</p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-green">Open</span>
                </a>
              ))}
              </div>

              <div className="mt-8 grid gap-4 lg:grid-cols-2">
                <a
                  href="https://wa.me/254717002340?text=Hi%20Trendy%20Quest!%20I%20need%20help%20with%20my%20order."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-3xl bg-[#25D366] p-5 text-white transition hover:bg-[#1ebe5d]"
                >
                  <div className="flex items-center gap-3">
                    <MessageCircle size={22} />
                    <p className="text-lg font-semibold">Chat on WhatsApp</p>
                  </div>
                  <p className="mt-3 text-sm leading-7 text-white/90">
                    Fast replies for orders, sizing, delivery, and product questions.
                  </p>
                </a>

                <a
                  href="mailto:pattohkarimi@gmail.com"
                  className="rounded-3xl border border-[#ebe3ce] bg-[#f8faf6] p-5 text-[#26452c] transition hover:border-green hover:bg-white"
                >
                  <div className="flex items-center gap-3">
                    <Mail size={22} />
                    <p className="text-lg font-semibold">Send Email</p>
                  </div>
                  <p className="mt-3 text-sm leading-7 text-[#5d6a60]">
                    Prefer email? Reach us directly for general enquiries and follow-up.
                  </p>
                </a>
              </div>

              <div className="mt-6 rounded-3xl border border-[#ebe3ce] bg-[#f8faf6] p-5 text-[#26452c] shadow-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-green">Find us on the map</p>
                <p className="mt-3 text-sm leading-7">
                  Visit our store location on Google Maps to get directions and see where we are based.
                </p>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Dubai+Merchant+Mall+Ronald+Ngala+Street+Shop+G18+Nairobi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center justify-center rounded-full bg-[#1a73e8] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#165ab8]"
                >
                  View location on Google Maps
                </a>
              </div>

              <div className="mt-6 rounded-3xl bg-[#295c33] p-6 text-white">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-white/80">Need help fast?</p>
                <div className="mt-3 space-y-2 text-sm leading-7 text-white/92">
                  <p className="flex items-center gap-2">
                    <Phone size={16} />
                    +254 717 002 340
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail size={16} />
                    pattohkarimi@gmail.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
